import React, { useEffect, useState } from 'react';
import { providerBookingApi } from '../api/providerBookingApi';
import { openInvoicePrint } from '../utils/invoicePrint';

// Amounts arrive from the backend already in dollars (formatInvoice → /100).
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const STATUS = {
  awaiting_release: { label: 'Awaiting release', className: 'bg-amber-50 text-amber-700' },
  released: { label: 'Released', className: 'bg-emerald-50 text-emerald-700' },
};

// Read-only Gigger invoice for one completed booking. The provider endpoint verifies
// that the signed-in Gigger owns the booking and can only return gigger_payout data.
const PayoutStatement = ({ bookingId }) => {
  const [inv, setInv] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | not_ready | error
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await providerBookingApi.payoutInvoice(bookingId);
        if (cancelled) return;
        setInv(res.data || null);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setState('not_ready');
        } else {
          setError(err.response?.data?.message || "Couldn't load your payout invoice.");
          setState('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  if (state === 'loading') return null;

  if (state === 'not_ready') {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="font-nunito text-sm font-bold text-giggs-ink">Gigger payout invoice</div>
        <div className="mt-2 text-[13px] leading-5 text-giggs-muted">
          Your payout invoice will appear here once the client has paid in full.
        </div>
      </section>
    );
  }

  if (state === 'error') {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="font-nunito text-sm font-bold text-giggs-ink">Gigger payout invoice</div>
        <div className="mt-2 text-[13px] text-red-600">{error}</div>
      </section>
    );
  }

  if (!inv) return null;
  const status = STATUS[inv.status] || STATUS.awaiting_release;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <img src="/images/logo.webp" alt="GIGGS" className="h-auto w-[96px]" />
          <div className="mt-3 font-nunito text-sm font-bold text-giggs-ink">Gigger payout invoice</div>
          <div className="mt-1 text-xs text-giggs-muted">{inv.invoiceNumber} · issued {fmt(inv.issuedAt)}</div>
        </div>
        <span className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-bold ${status.className}`}>{status.label}</span>
      </div>

      <div className="py-4">
        {(inv.lineItems || []).map((item, index) => (
          <div key={`${item.description}-${index}`} className="flex items-start justify-between gap-4 py-1.5 text-sm text-giggs-ink">
            <span className="min-w-0 break-words">{item.description}</span>
            <span className="shrink-0 font-semibold">{money(item.amount)}</span>
          </div>
        ))}
        <div className="mt-3 flex items-center justify-between gap-4 border-t border-slate-100 pt-3 text-[15px] font-extrabold text-giggs-ink">
          <span>Your earnings</span>
          <span className="text-giggs-green">{money(inv.total)}</span>
        </div>
      </div>

      <div className="text-xs leading-5 text-giggs-muted">
        {inv.status === 'released'
          ? `Released${inv.releasedAt ? ` on ${fmt(inv.releasedAt)}` : ''} — the transfer is on its way to your connected bank account.`
          : 'GIGGS will release your payout to your connected account after review. No action is needed from you.'}
      </div>

      <button
        type="button"
        onClick={() => openInvoicePrint({ invoice: inv, kind: 'gigger' })}
        className="mt-4 appearance-none cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-giggs-green transition hover:border-giggs-green"
      >
        View / Print invoice
      </button>
    </section>
  );
};

export default PayoutStatement;